import {
  ControlAssetLinksService,
  ControlAuditLinksService,
  ControlCatalogService,
  ControlFindingLinksService,
  ControlRequirementLinksService,
  ControlRiskLinksService,
  ControlsService,
  ControlVendorLinksService,
  EvidenceService,
} from "./generated";
import apiClient from "./apiClient";
import { withApi } from "./openapiClient";
import {
  fetchControlLinks,
  linkControlEntity as linkControlEntityService,
  unlinkControlEntity as unlinkControlEntityService,
} from "../services/controlLinks";

const toIsoString = (value) => {
  if (!value) return value;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00Z`).toISOString();
  }
  const asDate = value instanceof Date ? value : new Date(value);
  return Number.isNaN(asDate.getTime()) ? value : asDate.toISOString();
};

const normalizeControlPayload = (data = {}) => {
  const payload = { ...data };
  ["next_test_due", "last_test_date", "last_review_date"].forEach((field) => {
    if (field in payload) {
      payload[field] = payload[field] ? toIsoString(payload[field]) : null;
    }
  });
  ["owner_user_id", "assignee_user_id", "control_category_id"].forEach((field) => {
    if (payload[field] !== undefined && payload[field] !== null && payload[field] !== "") {
      payload[field] = Number(payload[field]);
    }
  });
  if (payload.risk_id !== undefined && payload.risk_id !== null) {
    payload.risk_id = Number(payload.risk_id);
  }
  if (Array.isArray(payload.risk_ids)) {
    payload.risk_ids = payload.risk_ids.map((id) => Number(id));
  }
  if (Array.isArray(payload.control_labels)) {
    payload.control_labels = payload.control_labels
      .map((label) => String(label || "").trim())
      .filter(Boolean);
  }
  return payload;
};

// Base Controls
export const getControls = (params = {}) =>
  withApi(
    apiClient
      .get("/controls/", {
        params: {
          owner: params.owner,
          status: params.status,
          category: params.category,
          catalog: params.catalog,
          health_reason: params.health_reason,
          as_of: params.as_of ?? params.asOf,
          q: params.q ?? params.search,
          limit: params.limit,
          offset: params.offset,
          sort: params.sort,
        },
      })
      .then((response) => response.data)
  );

export const getControlsHealth = (params = {}) =>
  withApi(
    apiClient
      .get("/controls/health", {
        params: {
          owner: params.owner,
          status: params.status,
          category: params.category,
          catalog: params.catalog,
          q: params.q ?? params.search,
          as_of: params.as_of ?? params.asOf,
        },
      })
      .then((response) => response.data)
  );

export const searchControls = (q) => getControls({ q });

export const listControlAssignmentUsers = () => apiClient.get("/controls/assignment-users");

export const getControl = (controlId) =>
  withApi(
    ControlsService.getControlControlsControlIdGet({
      controlId: Number(controlId),
    })
  );

export const createControl = (data) =>
  withApi(
    ControlsService.createControlControlsPost({
      requestBody: normalizeControlPayload(data),
    })
  );

export const updateControl = (controlId, data) =>
  withApi(
    ControlsService.updateControlControlsControlIdPut({
      controlId: Number(controlId),
      requestBody: normalizeControlPayload(data),
    })
  );

export const deleteControl = (controlId) =>
  withApi(
    ControlsService.deleteControlControlsControlIdDelete({
      controlId: Number(controlId),
    })
  );

const parseFilenameFromDisposition = (headerValue) => {
  const value = String(headerValue || "");
  if (!value) return null;

  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]).trim() || null;
    } catch (_err) {
      return utf8Match[1].trim() || null;
    }
  }

  const quotedMatch = value.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) {
    return quotedMatch[1].trim() || null;
  }

  const plainMatch = value.match(/filename=([^;]+)/i);
  return plainMatch?.[1]?.trim() || null;
};

const extractBlobErrorDetail = async (blob) => {
  if (!(blob instanceof Blob)) return "";
  try {
    const text = await blob.text();
    if (!text) return "";
    try {
      const parsed = JSON.parse(text);
      return parsed?.detail || parsed?.error || parsed?.message || text;
    } catch (_err) {
      return text;
    }
  } catch (_err) {
    return "";
  }
};

export const downloadControlBulkUploadTemplate = async () => {
  try {
    const response = await apiClient.get("/controls/bulk-upload/template", {
      responseType: "blob",
    });
    return {
      blob: response.data,
      filename:
        parseFilenameFromDisposition(response.headers?.["content-disposition"]) ||
        "control-bulk-upload-template.xlsx",
      contentType:
        response.headers?.["content-type"] ||
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    };
  } catch (error) {
    const detail =
      error?.detail ||
      (await extractBlobErrorDetail(error?.response?.data)) ||
      error?.message ||
      "Request failed";
    const normalized = error instanceof Error ? error : new Error(detail);
    normalized.message = detail;
    normalized.detail = detail;
    normalized.status = error?.status || error?.response?.status || null;
    normalized.path = error?.path || "/controls/bulk-upload/template";
    throw normalized;
  }
};

export const importControlBulkUploadWorkbook = async (file) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await apiClient.post("/controls/bulk-upload/import", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

// Aggregated links are not present in the OpenAPI contract; surface the gap explicitly.
export const getControlLinks = (controlId) => fetchControlLinks(controlId);
export const linkEntity = (controlId, entityType, entityId, data) =>
  linkControlEntityService(controlId, entityType, entityId, data);
export const unlinkEntity = (controlId, entityType, entityId) =>
  unlinkControlEntityService(controlId, entityType, entityId);

// Control -> Risk
export const getControlRisks = (controlId) =>
  withApi(
    ControlRiskLinksService.listRisksForControlControlsControlIdRisksGet({
      controlId: Number(controlId),
    })
  );
export const linkRisk = (controlId, riskId, data = {}) =>
  withApi(
    ControlRiskLinksService.linkControlToRiskControlsControlIdRisksRiskIdPost({
      controlId: Number(controlId),
      riskId: Number(riskId),
      createdBy: data?.created_by ?? data?.createdBy ?? undefined,
    })
  );
export const unlinkRisk = (controlId, riskId) =>
  withApi(
    ControlRiskLinksService.unlinkControlFromRiskControlsControlIdRisksRiskIdDelete({
      controlId: Number(controlId),
      riskId: Number(riskId),
    })
  );

// Control -> Vendor
export const getControlVendors = (controlId) =>
  withApi(
    ControlVendorLinksService.listVendorsForControlControlsControlIdVendorsGet({
      controlId: Number(controlId),
    })
  );
export const linkVendor = (controlId, vendorId, data = {}) =>
  withApi(
    ControlVendorLinksService.linkControlToVendorControlsControlIdVendorsVendorIdPost({
      controlId: Number(controlId),
      vendorId: Number(vendorId),
      createdBy: data?.created_by ?? data?.createdBy ?? undefined,
    })
  );
export const unlinkVendor = (controlId, vendorId) =>
  withApi(
    ControlVendorLinksService.unlinkControlFromVendorControlsControlIdVendorsVendorIdDelete({
      controlId: Number(controlId),
      vendorId: Number(vendorId),
    })
  );

// Control -> Policy
export const getControlPolicies = (controlId) =>
  withApi(
    apiClient
      .get(`/controls/${Number(controlId)}/tenant-policies`)
      .then((response) => response.data)
  );
export const linkPolicy = (controlId, policyId, data = {}) =>
  linkTenantPolicy(controlId, policyId, data);
export const unlinkPolicy = (controlId, policyId) =>
  unlinkTenantPolicy(controlId, policyId);
export const linkTenantPolicy = (controlId, tenantPolicyId, data = {}) =>
  withApi(
    apiClient
      .post(`/controls/${Number(controlId)}/tenant-policies/${Number(tenantPolicyId)}`, null, {
        params: {
          created_by: data?.created_by ?? data?.createdBy ?? undefined,
        },
      })
      .then((response) => response.data)
  );
export const unlinkTenantPolicy = (controlId, tenantPolicyId) =>
  withApi(
    apiClient
      .delete(`/controls/${Number(controlId)}/tenant-policies/${Number(tenantPolicyId)}`)
      .then((response) => response.data)
  );

// Control -> Requirement
export const getControlRequirements = (controlId) =>
  withApi(
    ControlRequirementLinksService.listRequirementsForControlControlsControlIdRequirementsGet({
      controlId: Number(controlId),
    })
  );
export const linkRequirement = (controlId, requirementId, data = {}) =>
  withApi(
    ControlRequirementLinksService.linkControlToRequirementControlsControlIdRequirementsRequirementIdPost({
      controlId: Number(controlId),
      requirementId: Number(requirementId),
      createdBy: data?.created_by ?? data?.createdBy ?? undefined,
    })
  );
export const unlinkRequirement = (controlId, requirementId) =>
  withApi(
    ControlRequirementLinksService.unlinkControlFromRequirementControlsControlIdRequirementsRequirementIdDelete({
      controlId: Number(controlId),
      requirementId: Number(requirementId),
    })
  );

// Compatibility helpers
export const listControlsByRisk = (riskId) =>
  withApi(
    ControlRiskLinksService.listControlRiskLinksControlsByRiskRiskIdGet({
      riskId: Number(riskId),
    })
  );
export const listRiskControls = (riskId) =>
  withApi(
    ControlRiskLinksService.listControlsForRiskControlsRisksRiskIdControlsGet({
      riskId: Number(riskId),
    })
  );
export const unlinkControlFromRisk = (controlId, riskId) => unlinkRisk(controlId, riskId);

// Tests
export const addControlTest = (controlId, data) =>
  withApi(
    ControlsService.addControlTestControlsControlIdTestsPost({
      controlId: Number(controlId),
      requestBody: {
        ...data,
        date: data?.date ? toIsoString(data.date) : null,
      },
    })
  );
export const listControlTests = (controlId) =>
  withApi(
    ControlsService.listControlTestsControlsControlIdTestsGet({
      controlId: Number(controlId),
    })
  );

// History
export const getControlHistory = (controlId) =>
  withApi(
    ControlsService.getControlHistoryControlsControlIdHistoryGet({
      controlId: Number(controlId),
    })
  );

// Evidence
export const addControlEvidence = (controlId, data = {}) =>
  withApi(
    EvidenceService.createEvidenceForControlControlsControlIdEvidencePost({
      controlId: Number(controlId),
      formData:
        data instanceof FormData
          ? data
          : {
              url_payload: data?.url ? { url: data.url } : data?.url_payload,
              file: data?.file,
            },
    })
  );
export const listControlEvidence = (controlId) =>
  withApi(
    EvidenceService.listControlEvidenceControlsControlIdEvidenceGet({
      controlId: Number(controlId),
    })
  );

// Policies (legacy names)
export const listPoliciesForControl = (controlId) => getControlPolicies(controlId);
export const linkControlToPolicy = (controlId, policyId, data) => linkPolicy(controlId, policyId, data);
export const unlinkControlPolicy = (controlId, policyId) => unlinkPolicy(controlId, policyId);
export const linkControlToTenantPolicy = (controlId, tenantPolicyId, data) =>
  linkTenantPolicy(controlId, tenantPolicyId, data);
export const unlinkControlTenantPolicy = (controlId, tenantPolicyId) =>
  unlinkTenantPolicy(controlId, tenantPolicyId);
export const listControlsForPolicy = (policyId) =>
  listControlsForTenantPolicy(policyId);
export const listControlsForTenantPolicy = (tenantPolicyId) =>
  withApi(
    apiClient
      .get(`/controls/tenant-policies/${Number(tenantPolicyId)}/controls`)
      .then((response) => response.data)
  );

// Risks (legacy names)
export const listRisksForControl = (controlId) => getControlRisks(controlId);
export const linkControlToRisk = (controlId, riskId, data) => linkRisk(controlId, riskId, data);
export const unlinkControlRisk = (controlId, riskId) => unlinkRisk(controlId, riskId);
export const listControlsForRisk = (riskId) => listRiskControls(riskId);

// Requirements (legacy names)
export const listRequirementsForControl = (controlId) => getControlRequirements(controlId);
export const linkControlToRequirement = (controlId, requirementId, data) =>
  linkRequirement(controlId, requirementId, data);
export const unlinkControlRequirement = (controlId, requirementId) =>
  unlinkRequirement(controlId, requirementId);
export const listControlsForRequirement = (requirementId) =>
  withApi(
    ControlRequirementLinksService.listControlsForRequirementControlsRequirementsRequirementIdControlsGet({
      requirementId: Number(requirementId),
    })
  );

// Vendors (legacy names)
export const listVendorsForControl = (controlId) => getControlVendors(controlId);
export const linkControlToVendor = (controlId, vendorId, data) => linkVendor(controlId, vendorId, data);
export const unlinkControlVendor = (controlId, vendorId) => unlinkVendor(controlId, vendorId);
export const listControlsForVendor = (vendorId) =>
  withApi(
    ControlVendorLinksService.listControlsForVendorControlsVendorsVendorIdControlsGet({
      vendorId: Number(vendorId),
    })
  );

// Assets
export const listAssetsForControl = (controlId) =>
  withApi(
    ControlAssetLinksService.listAssetsForControlControlsControlIdAssetsGet({
      controlId: Number(controlId),
    })
  );
export const linkControlToAsset = (controlId, assetId, data = {}) =>
  withApi(
    ControlAssetLinksService.linkControlToAssetControlsControlIdAssetsAssetIdPost({
      controlId: Number(controlId),
      assetId: Number(assetId),
      createdBy: data?.created_by ?? data?.createdBy ?? undefined,
    })
  );
export const unlinkControlAsset = (controlId, assetId) =>
  withApi(
    ControlAssetLinksService.unlinkControlFromAssetControlsControlIdAssetsAssetIdDelete({
      controlId: Number(controlId),
      assetId: Number(assetId),
    })
  );
export const listControlsForAsset = (assetId) =>
  withApi(
    ControlAssetLinksService.listControlsForAssetControlsAssetsAssetIdControlsGet({
      assetId: Number(assetId),
    })
  );

// Audits
export const listAuditsForControl = (controlId) =>
  withApi(
    ControlAuditLinksService.listAuditsForControlControlsControlIdAuditsGet({
      controlId: Number(controlId),
    })
  );
export const linkControlToAudit = (controlId, auditId, data = {}) =>
  withApi(
    ControlAuditLinksService.linkControlToAuditControlsControlIdAuditsAuditIdPost({
      controlId: Number(controlId),
      auditId: Number(auditId),
      createdBy: data?.created_by ?? data?.createdBy ?? undefined,
    })
  );
export const unlinkControlAudit = (controlId, auditId) =>
  withApi(
    ControlAuditLinksService.unlinkControlFromAuditControlsControlIdAuditsAuditIdDelete({
      controlId: Number(controlId),
      auditId: Number(auditId),
    })
  );
export const listControlsForAudit = (auditId) =>
  withApi(
    ControlAuditLinksService.listControlsForAuditControlsAuditsAuditIdControlsGet({
      auditId: Number(auditId),
    })
  );

// Findings
export const listFindingsForControl = (controlId) =>
  withApi(
    ControlFindingLinksService.listFindingsForControlControlsControlIdFindingsGet({
      controlId: Number(controlId),
    })
  );
export const linkControlToFinding = (controlId, findingId, data = {}) =>
  withApi(
    ControlFindingLinksService.linkControlToFindingControlsControlIdFindingsFindingIdPost({
      controlId: Number(controlId),
      findingId: Number(findingId),
      createdBy: data?.created_by ?? data?.createdBy ?? undefined,
    })
  );
export const unlinkControlFinding = (controlId, findingId) =>
  withApi(
    ControlFindingLinksService.unlinkControlFromFindingControlsControlIdFindingsFindingIdDelete({
      controlId: Number(controlId),
      findingId: Number(findingId),
    })
  );
export const listControlsForFinding = (findingId) =>
  withApi(
    ControlFindingLinksService.listControlsForFindingControlsFindingsFindingIdControlsGet({
      findingId: Number(findingId),
    })
  );

// Control Catalog
export const createCatalogControl = (data) =>
  withApi(
    ControlCatalogService.createCatalogControlControlLibraryPost({
      requestBody: data,
    })
  );
export const listCatalogControls = (params = {}) =>
  withApi(
    ControlCatalogService.listCatalogControlsControlLibraryGet({
      domain: params.domain,
      subdomain: params.subdomain,
      q: params.q,
    })
  );
export const getCatalogControl = (catalogId) =>
  withApi(
    ControlCatalogService.getCatalogControlControlLibraryCatalogIdGet({
      catalogId: Number(catalogId),
    })
  );
export const updateCatalogControl = (catalogId, data) =>
  withApi(
    ControlCatalogService.updateCatalogControlControlLibraryCatalogIdPut({
      catalogId: Number(catalogId),
      requestBody: data,
    })
  );
export const deleteCatalogControl = (catalogId) =>
  withApi(
    ControlCatalogService.deleteCatalogControlControlLibraryCatalogIdDelete({
      catalogId: Number(catalogId),
    })
  );
export const createCatalogMapping = (data) =>
  withApi(
    ControlCatalogService.createCatalogMappingControlLibraryMappingsPost({
      requestBody: data,
    })
  );
export const listCatalogMappings = (catalogId) =>
  withApi(
    ControlCatalogService.listCatalogMappingsControlLibraryCatalogIdMappingsGet({
      catalogId: Number(catalogId),
    })
  );
export const deleteCatalogMapping = (mappingId) =>
  withApi(
    ControlCatalogService.deleteCatalogMappingControlLibraryMappingsMappingIdDelete({
      mappingId: Number(mappingId),
    })
  );
